import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Star, ThumbsUp, MessageSquare, Loader2, CheckCircle2,
  TrendingUp, Users, BarChart3, Send
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';

const StarRating = ({ value, onChange, readonly = false, size = 'md' }) => {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`${sizeClass} transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          onClick={() => !readonly && onChange?.(star)}
        >
          <Star
            className={`${sizeClass} ${
              (hover || value) >= star
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export default function TripFeedback() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { isOrganizer, trip } = context || {};
  const { api, user } = useAuth();
  
  const [myFeedback, setMyFeedback] = useState(null);
  const [allFeedbacks, setAllFeedbacks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    overall_rating: 0,
    organization_rating: 0,
    itinerary_rating: 0,
    cost_benefit_rating: 0,
    highlights: '',
    improvements: '',
    would_travel_again: true,
  });

  useEffect(() => {
    fetchFeedback();
  }, [tripId]);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const [myRes, allRes] = await Promise.all([
        api.get(`/trips/${tripId}/feedback/my`),
        isOrganizer ? api.get(`/trips/${tripId}/feedback`) : Promise.resolve({ data: [] })
      ]);
      
      setMyFeedback(myRes.data);
      setAllFeedbacks(allRes.data || []);
      
      if (isOrganizer) {
        const summaryRes = await api.get(`/trips/${tripId}/feedback/summary`);
        setSummary(summaryRes.data);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (formData.overall_rating === 0) {
      toast.error('Dê uma nota geral para a viagem');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/trips/${tripId}/feedback`, formData);
      toast.success('Feedback enviado! Obrigado!');
      setDialogOpen(false);
      fetchFeedback();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const ratingLabels = {
    1: 'Ruim',
    2: 'Regular',
    3: 'Bom',
    4: 'Muito bom',
    5: 'Excelente'
  };

  return (
    <div className="space-y-6">
      {/* Header Padronizado */}
      <PageHeader
        title="Feedback"
        subtitle="Avalie a viagem e ajude a melhorar as próximas"
        helpScreen="feedback"
        actions={
          !myFeedback ? (
            <Button
              className="rounded-full bg-orange-500 hover:bg-orange-600"
              onClick={() => setDialogOpen(true)}
              data-testid="give-feedback-btn"
            >
              <Star className="w-4 h-4 mr-2" />
              Avaliar viagem
            </Button>
          ) : null
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <>
          {/* My Feedback */}
          {myFeedback && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    Seu feedback
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Geral</p>
                      <StarRating value={myFeedback.overall_rating} readonly />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Organização</p>
                      <StarRating value={myFeedback.organization_rating} readonly />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Roteiro</p>
                      <StarRating value={myFeedback.itinerary_rating} readonly />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Custo-benefício</p>
                      <StarRating value={myFeedback.cost_benefit_rating} readonly />
                    </div>
                  </div>
                  {myFeedback.highlights && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-green-700">Destaques:</p>
                      <p className="text-slate-600 dark:text-slate-400">{myFeedback.highlights}</p>
                    </div>
                  )}
                  {myFeedback.improvements && (
                    <div>
                      <p className="text-sm font-medium text-orange-700">Sugestões:</p>
                      <p className="text-slate-600 dark:text-slate-400">{myFeedback.improvements}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Summary for Organizer */}
          {isOrganizer && summary && summary.total_feedbacks > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border-slate-100 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-orange-500" />
                    Resumo dos Feedbacks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-500">{summary.average_overall}</div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Média geral</p>
                      <StarRating value={Math.round(summary.average_overall)} readonly size="sm" />
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-slate-700 dark:text-slate-300">{summary.total_feedbacks}</div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Respostas</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-500">{summary.would_travel_again_percentage}%</div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Viajariam novamente</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-sky-500">{summary.average_organization}</div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Organização</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1">
                        <ThumbsUp className="w-4 h-4 text-green-500" />
                        Destaques
                      </h4>
                      {summary.common_highlights.length > 0 ? (
                        <ul className="space-y-1">
                          {summary.common_highlights.map((h, i) => (
                            <li key={i} className="text-sm text-slate-600 bg-green-50 rounded-lg p-2">
                              "{h}"
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-slate-400">Nenhum destaque registrado</p>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1">
                        <TrendingUp className="w-4 h-4 text-orange-500" />
                        Sugestões de melhoria
                      </h4>
                      {summary.common_improvements.length > 0 ? (
                        <ul className="space-y-1">
                          {summary.common_improvements.map((imp, i) => (
                            <li key={i} className="text-sm text-slate-600 bg-orange-50 rounded-lg p-2">
                              "{imp}"
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-slate-400">Nenhuma sugestão registrada</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* No Feedback Yet */}
          {!myFeedback && !isOrganizer && (
            <Card className="border-slate-100 dark:border-slate-700 border-dashed">
              <CardContent className="py-12 text-center">
                <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
                  Como foi a viagem?
                </h3>
                <p className="text-slate-500 mb-4">
                  Seu feedback ajuda a melhorar as próximas aventuras!
                </p>
                <Button
                  className="rounded-full bg-orange-500 hover:bg-orange-600"
                  onClick={() => setDialogOpen(true)}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Avaliar agora
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Organizer: No feedbacks yet */}
          {isOrganizer && (!summary || summary.total_feedbacks === 0) && (
            <Card className="border-slate-100 dark:border-slate-700 border-dashed">
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
                  Nenhum feedback ainda
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Aguarde os participantes avaliarem a viagem
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Feedback Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Avaliar viagem</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Overall Rating */}
            <div className="text-center">
              <Label className="text-base">Como foi a viagem no geral?</Label>
              <div className="flex justify-center mt-3">
                <StarRating
                  value={formData.overall_rating}
                  onChange={(v) => setFormData({ ...formData, overall_rating: v })}
                  size="lg"
                />
              </div>
              {formData.overall_rating > 0 && (
                <p className="text-sm text-orange-600 mt-1">
                  {ratingLabels[formData.overall_rating]}
                </p>
              )}
            </div>

            {/* Detailed Ratings */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label className="text-sm">Organização</Label>
                <div className="flex items-center justify-between mt-1">
                  <StarRating
                    value={formData.organization_rating}
                    onChange={(v) => setFormData({ ...formData, organization_rating: v })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Roteiro</Label>
                <div className="flex items-center justify-between mt-1">
                  <StarRating
                    value={formData.itinerary_rating}
                    onChange={(v) => setFormData({ ...formData, itinerary_rating: v })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Custo-benefício</Label>
                <div className="flex items-center justify-between mt-1">
                  <StarRating
                    value={formData.cost_benefit_rating}
                    onChange={(v) => setFormData({ ...formData, cost_benefit_rating: v })}
                  />
                </div>
              </div>
            </div>

            {/* Comments */}
            <div>
              <Label>O que você mais gostou?</Label>
              <Textarea
                value={formData.highlights}
                onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                placeholder="Conte os destaques da viagem..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label>O que poderia melhorar?</Label>
              <Textarea
                value={formData.improvements}
                onChange={(e) => setFormData({ ...formData, improvements: e.target.value })}
                placeholder="Sugestões para próximas viagens..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Would travel again */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="would_travel_again"
                checked={formData.would_travel_again}
                onCheckedChange={(checked) => setFormData({ ...formData, would_travel_again: checked })}
              />
              <Label htmlFor="would_travel_again" className="font-normal">
                Viajaria novamente com este grupo
              </Label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || formData.overall_rating === 0}
                className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
